import React from "react"
import { useTranslation } from "react-i18next"
import ModalShell from "../ModalShell"
import { motion } from "framer-motion"

const cleanColor = (val) => (val ? String(val).trim() : "")

const ConfirmModal = ({
  type,
  data,
  onConfirm,
  onClose,
  statusTabs = [],
  isConfirming = false,
  errorMessage = null,
}) => {
  const { t } = useTranslation()
  const isDelete = type === "delete_one" || type === "delete_bulk"
  const status =
    type === "status_bulk" ? statusTabs.find((s) => String(s.id) === String(data.status)) : null

  const title =
    type === "delete_one"
      ? t("admin.confirm_delete_one_title")
      : type === "delete_bulk"
        ? t("admin.confirm_delete_bulk_title", { count: data.count })
        : t("admin.confirm_status_bulk_title", { count: data.count })

  const subtitle =
    type === "delete_one"
      ? t("admin.confirm_delete_one_subtitle", { id: data.rowId })
      : type === "delete_bulk"
        ? t("admin.confirm_delete_bulk_subtitle", { count: data.count })
        : t("admin.confirm_status_bulk_subtitle")

  return (
    <ModalShell onClose={isConfirming ? () => {} : onClose} zIndex={300} maxWidth="sm:max-w-sm">
      <div className="bg-white w-full rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${isDelete ? "bg-red-50" : "bg-indigo-50"}`}
          >
            {isDelete ? (
              <svg
                className="w-7 h-7 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            ) : (
              <svg
                className="w-7 h-7 text-indigo-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            )}
          </div>
          <h2 className="text-base font-semibold text-gray-900 mb-1">{title}</h2>
          <p className="text-sm text-gray-500">{subtitle}</p>
          {status && (
            <div className="mt-3">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium border"
                style={{
                  backgroundColor: cleanColor(status.background) || "#ffffff",
                  color: cleanColor(status.color) || "#374151",
                  borderColor: cleanColor(status.color) || "#e5e7eb",
                }}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cleanColor(status.color) || "#9ca3af" }}
                />
                {status.label}
              </span>
            </div>
          )}
          {type === "delete_bulk" && (
            <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5 text-left w-full">
              <svg
                className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {/* <span className="text-xs text-red-600">
                {t("filter.confirm_delete_irreversible")}
              </span> */}
            </div>
          )}
          {errorMessage && (
            <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5 text-left w-full">
              <svg
                className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-xs text-red-600">{errorMessage}</span>
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex items-center gap-3">
          <button
            onClick={onClose}
            disabled={isConfirming}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("admin.cancel")}
          </button>
          <motion.button
            whileHover={{ scale: isConfirming ? 1 : 1.02 }}
            whileTap={{ scale: isConfirming ? 1 : 0.98 }}
            onClick={onConfirm}
            disabled={isConfirming}
            className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium text-white transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2
              ${isDelete ? "bg-red-500 hover:bg-red-600 shadow-red-200" : "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200"}`}
          >
            {isConfirming && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            )}
            {isConfirming
              ? t("admin.loading")
              : isDelete
                ? t("admin.deleted")
                : t("request_form.confirm")}
          </motion.button>
        </div>
      </div>
    </ModalShell>
  )
}

export default ConfirmModal
